import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import JobsService, { JobData } from '../api/JobsService';
import { EnrichedInterviewData } from './useInterviewManagement';

export const useInterviewJobsForDropdown = (interviews: EnrichedInterviewData[], companyId: string | number) => {
  const [dropdownJobs, setDropdownJobs] = useState<JobData[]>([]);
  const [isLoadingDropdownJobs, setIsLoadingDropdownJobs] = useState(false);
  const jobCacheRef = useRef<Map<number, JobData>>(new Map());
  const fetchingJobsRef = useRef<Set<number>>(new Set());

  // Extract unique job IDs from interviews
  const uniqueJobIds = useMemo(() => {
    const jobIds = Array.from(new Set(interviews.map(interview => interview.jobId)));
    console.log(`🎯 Found ${jobIds.length} unique job IDs for dropdown:`, jobIds);
    return jobIds;
  }, [interviews]);

  const fetchJobsForDropdown = useCallback(async (jobIds: number[]) => {
    if (jobIds.length === 0) return;
    
    console.log(`🚀 FAST: Starting job fetch for ${jobIds.length} jobs`);
    setIsLoadingDropdownJobs(true);
    
    const startTime = Date.now();
    const cachedJobs: JobData[] = [];
    const missingIds: number[] = [];
    
    // Check cache first
    jobIds.forEach(id => {
      if (jobCacheRef.current.has(id)) {
        cachedJobs.push(jobCacheRef.current.get(id)!);
      } else if (!fetchingJobsRef.current.has(id)) {
        missingIds.push(id);
        fetchingJobsRef.current.add(id);
      }
    });
    
    console.log(`📋 FAST: ${cachedJobs.length} cached, ${missingIds.length} to fetch`);
    
    // Show cached jobs immediately
    if (cachedJobs.length > 0) {
      setDropdownJobs(cachedJobs);
    }
    
    // OPTIMIZATION 1: Fetch ALL missing jobs in parallel (no batching delays)
    if (missingIds.length > 0) {
      try {
        // OPTIMIZATION 2: Use Promise.allSettled for better error handling
        const fetchPromises = missingIds.map(async (jobId) => {
          try {
            const job = await JobsService.getJobById(jobId);
            console.log(`✅ FAST: Fetched ${jobId}: "${job.title}"`);
            
            // Add to cache immediately
            jobCacheRef.current.set(jobId, job);
            fetchingJobsRef.current.delete(jobId);
            
            return { success: true, job, jobId };
          } catch (error) {
            console.warn(`❌ Failed to fetch job ${jobId}:`, error);
            fetchingJobsRef.current.delete(jobId);
            return { success: false, error, jobId };
          }
        });
        
        // OPTIMIZATION 3: Process results as they complete (not waiting for all)
        let processedCount = 0;
        const totalJobs = fetchPromises.length;
        
        // Update UI incrementally as jobs are fetched
        const updateInterval = setInterval(() => {
          const completedJobs: JobData[] = [];
          
          // Check which promises have resolved
          fetchPromises.forEach((promise) => {
            promise.then((result) => {
              if (result.success && result.job) {  // Add explicit job check
                setDropdownJobs(prev => {
                  const existingIds = new Set(prev.map(job => job.id));
                  if (!existingIds.has(result.job!.id)) {  // Use non-null assertion since we checked above
                    console.log(`⚡ FAST: +1 job (${prev.length + 1}/${cachedJobs.length + totalJobs})`);
                    return [...prev, result.job!].sort((a, b) => 
                      new Date(b.created).getTime() - new Date(a.created).getTime()
                    );
                  }
                  return prev;
                });
              }
            }).catch((error) => {
              console.warn('Promise error:', error);
            });
          });
          
          // Add new jobs to dropdown
          if (completedJobs.length > 0) {
            setDropdownJobs(prev => {
              const existingIds = new Set(prev.map(job => job.id));
              const newJobs = completedJobs.filter(job => !existingIds.has(job.id));
              
              if (newJobs.length > 0) {
                console.log(`⚡ FAST: +${newJobs.length} jobs (${prev.length + newJobs.length}/${cachedJobs.length + totalJobs})`);
                return [...prev, ...newJobs].sort((a, b) => 
                  new Date(b.created).getTime() - new Date(a.created).getTime()
                );
              }
              return prev;
            });
          }
          
          // Stop checking when all are done
          if (processedCount >= totalJobs) {
            clearInterval(updateInterval);
          }
        }, 50); // Check every 50ms for new results
        
        // Wait for all to complete (but UI updates happen incrementally)
        await Promise.allSettled(fetchPromises);
        clearInterval(updateInterval);
        
        console.log(`🚀 FAST: All jobs fetched in ${Date.now() - startTime}ms`);
        
      } catch (error) {
        console.error('Error in fast job fetching:', error);
        missingIds.forEach(id => fetchingJobsRef.current.delete(id));
      }
    }
    
    setIsLoadingDropdownJobs(false);
  }, []);

  // Fetch jobs when interviews change
  const uniqueJobIdsRef = useRef<number[]>([]);

  useEffect(() => {
    // Only update if the actual IDs changed (not just the array reference)
    const idsChanged = 
      uniqueJobIds.length !== uniqueJobIdsRef.current.length ||
      uniqueJobIds.some((id, i) => id !== uniqueJobIdsRef.current[i]);
    
    if (!idsChanged) {
      // IDs haven't changed, don't re-fetch
      return;
    }
    
    // Update the ref to track current IDs
    uniqueJobIdsRef.current = uniqueJobIds;
    
    if (uniqueJobIds.length > 0) {
      console.log(`🚀 FAST: Triggering optimized job fetch`);
      fetchJobsForDropdown(uniqueJobIds);
    } else {
      // Clear jobs when no interviews
      setDropdownJobs([]);
    }
  }, [uniqueJobIds, fetchJobsForDropdown]);

  return {
    dropdownJobs,
    isLoadingDropdownJobs,
    getJobById: useCallback((jobId: number) => {
      return jobCacheRef.current.get(jobId);
    }, [])
  };
};